import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ServiceHistoryButton } from "@/components/ServiceHistoryButton";
import { CodiceFiscaleButton } from "@/components/CodiceFiscaleButton";
import { Wifi, Radio, Signal, Cable, Search, CheckCircle2, XCircle, ArrowLeft, ArrowRight, Loader2, Flame, MapPin, Upload, X } from "lucide-react";
import { AddressFields, customerAddress } from "@/components/AddressFields";
import { FieldError, errCls, focusFirstError } from "@/components/FieldError";

const CF_RE = /^[A-Za-z]{6}\d{2}[A-Za-z]\d{2}[A-Za-z]\d{3}[A-Za-z]$/;
const validCf = (v) => CF_RE.test((v || "").trim());
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const internetErrors = (form, custType, actMode, bill, docFile) => {
  const e = {};
  const blank = (k) => !String(form[k] || "").trim();
  if (custType === "business") { if (blank("ragione_sociale")) e.ragione_sociale = "Campo obbligatorio"; if (blank("piva")) e.piva = "Campo obbligatorio"; }
  else if (blank("nome")) e.nome = "Campo obbligatorio";
  if (blank("email")) e.email = "Campo obbligatorio"; else if (!EMAIL_RE.test(form.email.trim())) e.email = "Email non valida";
  if (blank("telefono")) e.telefono = "Campo obbligatorio";
  if (actMode === "migration") {
    if (!validCf(form.codice_fiscale)) e.codice_fiscale = blank("codice_fiscale") ? "Obbligatorio per la migrazione" : "Formato non valido (16 caratteri)";
    if (!bill) e.bill = "Carica l'ultima bolletta";
    if (!docFile) e.doc = "Carica il documento del titolare";
  }
  return e;
};
const toB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

const TECHS = [
  { key: "fibra", label: "Fibra", icon: Cable, desc: "Fibra ottica FTTH/FTTC" },
  { key: "satellite", label: "Satellite (Starlink)", icon: Radio, desc: "Copertura ovunque via satellite" },
  { key: "wifi", label: "Wi-Fi (FWA)", icon: Wifi, desc: "Internet senza fili radio" },
  { key: "sim", label: "SIM Dati", icon: Signal, desc: "Connessione mobile 4G/5G" },
];
const EMPTY = { nome: "", cognome: "", ragione_sociale: "", piva: "", codice_fiscale: "", email: "", telefono: "", indirizzo: "", cap: "", citta: "", provincia: "" };

export default function InternetWizard() {
  const [step, setStep] = useState(1);
  const [tech, setTech] = useState("");
  const [address, setAddress] = useState("");
  const [checking, setChecking] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [coords, setCoords] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offerId, setOfferId] = useState("");
  const [custType, setCustType] = useState("consumer");
  const [mode, setMode] = useState("new");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [actMode, setActMode] = useState("new"); // new | migration
  const [bill, setBill] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [tried, setTried] = useState(false);
  const errs = internetErrors(form, custType, actMode, bill, docFile);
  const show = (k) => (tried || (k === "codice_fiscale" && form.codice_fiscale) ? errs[k] || "" : "");

  useEffect(() => {
    api.get("/internet/offers").then((r) => setOffers(r.data.offers || [])).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => { const v = e.target.value; setForm((s) => ({ ...s, [k]: v })); };
  const needsCoverage = tech === "fibra";

  const runCoverage = async (c = coords) => {
    if (!address.trim()) { toast.error("Inserisci l'indirizzo"); return; }
    setChecking(true);
    try {
      const params = { address: address.trim(), technology: tech };
      if (c) { params.lat = c.lat; params.lon = c.lon; }
      const { data } = await api.get("/internet/coverage", { params, timeout: 22000 });
      setCoverage(data);
    } catch (e) {
      // Se la mappa di copertura è troppo lenta o non raggiungibile, ricadi sulla stima positiva.
      if (e.code === "ECONNABORTED" || !e.response) {
        setCoverage({ covered: true, source: "stimato", message: "Copertura stimata: OK al 99% — è possibile procedere alla richiesta. La disponibilità tecnica sarà confermata in fase di attivazione." });
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore verifica");
      }
    }
    finally { setChecking(false); }
  };
  const searchAddr = async () => {
    if (address.trim().length < 3) { toast.error("Scrivi almeno 3 caratteri"); return; }
    setSearching(true); setCoverage(null); setCoords(null);
    try {
      const { data } = await api.get("/internet/address-search", { params: { q: address.trim() } });
      setSuggestions(data.results || []);
      if (!(data.results || []).length) toast.info("Nessun indirizzo trovato: puoi verificare comunque a mano");
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  };
  const selectSuggestion = (s) => { setAddress(s.label); setCoords({ lat: s.lat, lon: s.lon }); setSuggestions([]); runCoverage({ lat: s.lat, lon: s.lon }); };

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id); if (!c) return;
    const a = customerAddress(c);
    setCustomerId(id);
    setForm({ ...EMPTY, nome: c.name || c.nome || "", email: c.email || "", telefono: c.phone || c.telefono || "", codice_fiscale: c.codice_fiscale || "", indirizzo: a.address, cap: a.cap, citta: a.city, provincia: a.province });
  };

  const submit = async () => {
    if (Object.keys(errs).length) { setTried(true); toast.error("Completa i campi evidenziati in rosso"); focusFirstError(); return; }
    setBusy(true);
    try {
      const payload = {
        technology: tech, offer_id: offerId, address, customer_type: custType,
        customer_id: mode === "existing" ? customerId : null, is_new_customer: mode === "new",
        ...form, coverage_ok: coverage ? coverage.covered : null, activation_mode: actMode,
      };
      if (actMode === "migration") {
        payload.bill_base64 = await toB64(bill); payload.bill_filename = bill.name;
        payload.doc_base64 = await toB64(docFile); payload.doc_filename = docFile.name;
      }
      await api.post("/internet/requests", payload);
      setDone(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio richiesta"); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep(1); setTech(""); setAddress(""); setCoverage(null); setSuggestions([]); setCoords(null); setOfferId(""); setCustType("consumer"); setMode("new"); setCustomerId(""); setForm(EMPTY); setDone(false); setActMode("new"); setBill(null); setDocFile(null); };

  if (done) return (
    <div className="max-w-lg mx-auto text-center space-y-4 py-10" data-testid="internet-done">
      <div className="w-16 h-16 rounded-full bg-teal-500/10 text-teal-500 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9" /></div>
      <h1 className="font-head text-2xl font-bold">Richiesta di attivazione inviata!</h1>
      <p className="text-muted-foreground">La richiesta Internet è stata registrata e sarà gestita dall'operatore.</p>
      <Button onClick={reset} data-testid="internet-new">Nuova richiesta</Button>
      <ServiceHistoryButton service="internet" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="internet-wizard-page">
      <ServiceHistoryButton service="internet" />
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Connettività</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Wifi className="w-7 h-7 text-primary" /> Internet & Connettività</h1>
        <p className="text-sm text-muted-foreground mt-1">Passo {step} di 4 — {["Tecnologia", "Copertura", "Offerta", "Cliente"][step - 1]}.</p>
      </div>

      {step === 1 && (
        <Card className="p-6 glow-border space-y-4" data-testid="internet-step1">
          <p className="font-head font-semibold">Scegli la tecnologia</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {TECHS.map((t) => (
              <button key={t.key} type="button" onClick={() => setTech(t.key)} data-testid={`internet-tech-${t.key}`}
                className={`text-left rounded-xl border p-4 transition-colors ${tech === t.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                <t.icon className="w-6 h-6 text-primary" />
                <p className="font-semibold mt-2">{t.label}</p>
                <p className="text-xs text-muted-foreground">{t.desc}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-end"><Button disabled={!tech} onClick={() => { setStep(2); setCoverage(null); }} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="internet-next-1">Avanti <ArrowRight className="w-4 h-4" /></Button></div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 glow-border space-y-4" data-testid="internet-step2">
          <button onClick={() => setStep(1)} data-testid="internet-back-to-technology" className="app-text-button flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Indietro</button>
          <p className="font-head font-semibold">Verifica copertura — {TECHS.find((t) => t.key === tech)?.label}</p>
          <div className="flex gap-2">
            <Input placeholder="Cerca indirizzo reale (via, civico, città)" value={address} onChange={(e) => { setAddress(e.target.value); setCoords(null); }} onKeyDown={(e) => { if (e.key === "Enter") searchAddr(); }} data-testid="internet-address" />
            <Button variant="outline" onClick={searchAddr} disabled={searching} className="gap-2 shrink-0" data-testid="internet-search-btn">{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Cerca</Button>
          </div>
          {suggestions.length > 0 && (
            <div className="rounded-lg border border-border divide-y divide-border overflow-hidden" data-testid="internet-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} type="button" onClick={() => selectSuggestion(s)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2" data-testid={`internet-suggestion-${i}`}><MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> {s.label}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button next={!(coverage && coverage.covered)} onClick={() => runCoverage()} disabled={checking || !address.trim()} variant="secondary" className="gap-2" data-testid="internet-check-btn">{checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verifica copertura</Button>
            {coords && <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" /> Indirizzo geolocalizzato</Badge>}
          </div>
          {checking && <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="internet-check-wait"><Loader2 className="w-3 h-3 animate-spin" /> Verifica copertura in corso, può richiedere fino a ~20 secondi…</p>}
          {coverage && (
            <div className={`rounded-lg border p-3 text-sm ${coverage.covered ? "border-teal-500/50 text-teal-600 bg-teal-500/5" : "border-red-500/50 text-red-500 bg-red-500/5"}`} data-testid="internet-coverage-result">
              <div className="flex items-center gap-2">{coverage.covered ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {coverage.message}</div>
              {coverage.source && <p className="text-[11px] text-muted-foreground mt-1">Fonte: {coverage.source}</p>}
            </div>
          )}
          <div className="flex justify-end"><Button disabled={!address.trim() || (needsCoverage && !(coverage && coverage.covered))} onClick={() => setStep(3)} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="internet-next-2">Avanti <ArrowRight className="w-4 h-4" /></Button></div>
          {needsCoverage && coverage && !coverage.covered && <p className="text-xs text-muted-foreground">Per la fibra serve copertura positiva. Torna indietro e scegli Satellite o Wi-Fi.</p>}
        </Card>
      )}

      {step === 3 && (
        <Card className="p-6 glow-border space-y-4" data-testid="internet-step3">
          <button onClick={() => setStep(2)} data-testid="internet-back-to-coverage" className="app-text-button flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Indietro</button>
          <p className="font-head font-semibold">Scegli l'offerta</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {offers.map((o) => (
              <button key={o.id} type="button" onClick={() => setOfferId(o.id)} data-testid={`internet-offer-${o.id}`}
                className={`text-left rounded-xl border p-4 transition-colors ${offerId === o.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                <p className="font-semibold">{o.name}</p>
                <p className="font-head text-2xl font-bold mt-1">{euro(o.price)}<span className="text-xs font-normal text-muted-foreground">/mese</span></p>
                <p className="text-xs text-muted-foreground mt-1">{o.description}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-end"><Button disabled={!offerId} onClick={() => setStep(4)} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="internet-next-3">Avanti <ArrowRight className="w-4 h-4" /></Button></div>
        </Card>
      )}

      {step === 4 && (
        <Card className="p-6 glow-border space-y-4" data-testid="internet-step4">
          <button onClick={() => setStep(3)} data-testid="internet-back-to-offers" className="app-text-button flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Indietro</button>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={custType === "consumer" ? "default" : "outline"} onClick={() => setCustType("consumer")} data-testid="internet-type-consumer">Consumer</Button>
            <Button type="button" size="sm" variant={custType === "business" ? "default" : "outline"} onClick={() => setCustType("business")} data-testid="internet-type-business">Business</Button>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={mode === "new" ? "default" : "outline"} onClick={() => { setMode("new"); setForm(EMPTY); }} data-testid="internet-mode-new">Nuovo cliente</Button>
            <Button type="button" size="sm" variant={mode === "existing" ? "default" : "outline"} onClick={() => setMode("existing")} data-testid="internet-mode-existing">Cliente registrato</Button>
          </div>
          {mode === "existing" && (
            <div><Label>Seleziona cliente</Label>
              <Select value={customerId} onValueChange={pickCustomer}>
                <SelectTrigger className="mt-1.5" data-testid="internet-customer-select"><SelectValue placeholder="Scegli un cliente…" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.nome} · {c.email || <PhoneDisplay value={c.phone} data-testid={`internet-customer-phone-${c.id}`} />}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {custType === "business" && <>
              <div><Label>Ragione sociale *</Label><Input className={`mt-1.5 ${errCls(show("ragione_sociale"))}`} aria-invalid={!!show("ragione_sociale")} data-testid="internet-ragione" value={form.ragione_sociale} onChange={set("ragione_sociale")} /><FieldError msg={show("ragione_sociale")} testid="internet-ragione-error" /></div>
              <div><Label>P.IVA *</Label><Input className={`mt-1.5 font-mono ${errCls(show("piva"))}`} aria-invalid={!!show("piva")} data-testid="internet-piva" value={form.piva} onChange={set("piva")} /><FieldError msg={show("piva")} testid="internet-piva-error" /></div>
            </>}
            <div><Label>Nome {custType === "consumer" ? "*" : "referente"}</Label><Input className={`mt-1.5 ${errCls(show("nome"))}`} aria-invalid={!!show("nome")} data-testid="internet-nome" value={form.nome} onChange={set("nome")} /><FieldError msg={show("nome")} testid="internet-nome-error" /></div>
            <div><Label>Cognome</Label><Input className="mt-1.5" data-testid="internet-cognome" value={form.cognome} onChange={set("cognome")} /></div>
            <div><Label className="flex items-center gap-1">Codice fiscale {actMode === "migration" && <span className="text-destructive">*</span>}<CodiceFiscaleButton onResult={(cf) => setForm((s) => ({ ...s, codice_fiscale: (cf || "").toUpperCase() }))} defaultNome={form.nome} defaultCognome="" variant="ghost" size="sm" className="ml-auto h-6 px-2 text-xs" /></Label><Input className={`mt-1.5 font-mono ${errCls(show("codice_fiscale"))}`} aria-invalid={!!show("codice_fiscale")} data-testid="internet-cf" value={form.codice_fiscale} onChange={(e) => setForm((s) => ({ ...s, codice_fiscale: e.target.value.toUpperCase() }))} /><FieldError msg={show("codice_fiscale")} testid="internet-cf-error" /></div>
            <div><Label>Email *</Label><Input type="email" className={`mt-1.5 ${errCls(show("email"))}`} aria-invalid={!!show("email")} data-testid="internet-email" value={form.email} onChange={set("email")} /><FieldError msg={show("email")} testid="internet-email-error" /></div>
            <div><Label>Telefono *</Label><Input type="tel" className={`mt-1.5 ${errCls(show("telefono"))}`} aria-invalid={!!show("telefono")} data-testid="internet-telefono" value={form.telefono} onChange={set("telefono")} /><FieldError msg={show("telefono")} testid="internet-telefono-error" /></div>
          </div>
          <div className="space-y-3">
            <Label>Indirizzo di installazione</Label>
            <AddressFields prefix="internet-addr" addressLabel="Indirizzo (via e civico)"
              values={{ address: form.indirizzo, cap: form.cap, city: form.citta, province: form.provincia }}
              onChange={(p) => setForm((s) => ({ ...s, ...("address" in p ? { indirizzo: p.address } : {}), ...("cap" in p ? { cap: p.cap } : {}), ...("city" in p ? { citta: p.city } : {}), ...("province" in p ? { provincia: p.province } : {}) }))} />
          </div>
          <div className="space-y-3">
            <Label>Tipo di richiesta</Label>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={actMode === "new" ? "default" : "outline"} onClick={() => setActMode("new")} data-testid="internet-actmode-new">Nuova attivazione</Button>
              <Button type="button" size="sm" variant={actMode === "migration" ? "default" : "outline"} onClick={() => setActMode("migration")} data-testid="internet-actmode-migration">Migrazione / Portabilità</Button>
            </div>
            {actMode === "migration" && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-3" data-testid="internet-migration-docs">
                <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Documenti obbligatori per la migrazione</p>
                {[["Ultima bolletta operatore attuale *", bill, setBill, "internet-bill", "bill"], ["Documento d'identità del titolare *", docFile, setDocFile, "internet-doc", "doc"]].map(([lbl, val, setter, tid, ek]) => (
                  <div key={tid}>
                    <Label>{lbl}</Label>
                    {!val ? (
                      <label aria-invalid={!!show(ek)} className={`mt-1.5 flex items-center gap-2 cursor-pointer rounded-lg border border-dashed px-3 py-2.5 text-sm text-muted-foreground hover:border-primary ${show(ek) ? "border-destructive" : "border-border"}`} data-testid={`${tid}-upload`}>
                        <Upload className="w-4 h-4" /> Carica file (PDF/JPG/PNG)
                        <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => e.target.files?.[0] && setter(e.target.files[0])} />
                      </label>
                    ) : (
                      <div className="mt-1.5 flex items-center justify-between rounded-lg border border-teal-500/40 bg-teal-500/5 px-3 py-2.5 text-sm">
                        <span className="flex items-center gap-2 truncate"><CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" /> {val.name}</span>
                        <button type="button" onClick={() => setter(null)} className="text-muted-foreground hover:text-destructive" data-testid={`${tid}-clear`}><X className="w-4 h-4" /></button>
                      </div>
                    )}
                    <FieldError msg={show(ek)} testid={`${tid}-error`} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-lg border border-border p-3 text-sm text-muted-foreground" data-testid="internet-recap">
            {TECHS.find((t) => t.key === tech)?.label} · {offers.find((o) => o.id === offerId)?.name} ({euro(offers.find((o) => o.id === offerId)?.price || 0)}/mese) · {address}
          </div>
          <Button onClick={submit} disabled={busy} className="gap-2" data-testid="internet-submit"><Flame className="w-4 h-4" /> {busy ? "Invio…" : "Invia richiesta di attivazione"}</Button>
        </Card>
      )}
    </div>
  );
}
